/* eslint-disable no-undef */
import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useCurrentUser } from '../../hooks/useApi';
import { useSingleMarketAccess } from '../../hooks/useMarketAccess';
import { useMarketDetails } from '../../hooks/useMarketCompassApi';
import { LatestInsightsSection, PodcastsSection } from '../../components/markets';
import { KeyFindingsSection } from '@horwath-htl/shared-ui';
import { useMarketKeyFindings } from '../../hooks/useKeyFindings';
import HotelIcon from '@mui/icons-material/Hotel';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import DescriptionIcon from '@mui/icons-material/Description';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import { MarketPerformanceDashboard } from '../../components/markets/analytics/MarketPerformanceDashboard';
import { brandColors } from '../../theme';
import { AppBreadcrumbs } from '../../components/navigation/AppBreadcrumbs';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`market-tabpanel-${index}`}
      aria-labelledby={`market-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `market-tab-${index}`,
    'aria-controls': `market-tabpanel-${index}`,
  };
}

const MarketPage: React.FC = () => {
  const { marketId } = useParams<{ marketId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: currentUser, isLoading: isLoadingUser } = useCurrentUser();
  const [tabValue, setTabValue] = React.useState(0);
  
  // Convert marketId to number for the access check
  const marketIdNum = React.useMemo(() => {
    return marketId ? parseInt(marketId, 10) : undefined;
  }, [marketId]);
  
  // Memoize the enabled flag
  const isEnabled = React.useMemo(() => {
    return !!currentUser && !!marketIdNum && !isNaN(marketIdNum);
  }, [currentUser, marketIdNum]);
  
  // Check if user has access to this market
  const { hasAccess, loading, error } = useSingleMarketAccess(marketIdNum, {
    enabled: isEnabled,
  });

  // Fetch market details (must be called before any conditional returns)
  const { data: marketDetails, isLoading: isLoadingDetails, error: detailsError } = useMarketDetails(marketId);
  
  // Fetch key findings for the last 12 months
  const marketKeyFindingsQuery = useMarketKeyFindings(marketIdNum || 0, 12);
  

  // If user is still loading
  if (isLoadingUser) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading user data...</Typography>
        </Box>
      </Container>
    );
  }
  
  // If user is not authenticated (currentUser is null and not loading)
  if (!currentUser && !isLoadingUser) {
    // This shouldn't happen as ProtectedRoute should handle this, but just in case
    navigate('/login', { state: { from: location.pathname + location.search } });
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography sx={{ mt: 2 }}>Redirecting to login...</Typography>
        </Box>
      </Container>
    );
  }
  
  // If currentUser exists but might be empty or invalid
  if (!currentUser) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          <Alert severity="error">
            Unable to load user information. Please try refreshing the page.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  // If market ID is invalid
  if (!marketIdNum || isNaN(marketIdNum)) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          <Alert severity="error">
            Invalid market ID provided.
          </Alert>
        </Box>
      </Container>
    );
  }

  // If still checking access
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Checking market access...</Typography>
        </Box>
      </Container>
    );
  }

  // If there was an error checking access
  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          <Alert severity="error">
            Error checking market access. Please try again later.
          </Alert>
        </Box>
      </Container>
    );
  }

  // If user doesn't have access
  if (!hasAccess) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 6 }}>
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom color="error">
              Access Denied
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3 }}>
              You don't have an active subscription to this market.
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              To access this market's data and insights, you need an active subscription.
            </Alert>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                component={Link}
                to={`/global-market-reports?marketId=${marketId}`}
                sx={{ minWidth: 200 }}
              >
                View in Market Store
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => navigate('/subscriptions')}
              >
                My Subscriptions
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  // User has access - show market content
  
  // Final check - if we've gotten here, we should render something
  if (!hasAccess && !loading && !error) {
    console.error('MarketPage: Unexpected state - no access, no loading, no error');
  }
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        {/* Loading state for market details */}
        {isLoadingDetails && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Loading market details...</Typography>
          </Box>
        )}

        {/* Error state for market details */}
        {detailsError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load market details. Please try again.
          </Alert>
        )}

        {/* Market Details Display */}
        {marketDetails && (
          <>
            {/* Market Header */}
            <Box sx={{ mb: 4 }}>
              <Typography
                component="h1"
                sx={{
                  color: brandColors.blue,
                  fontWeight: 400,
                  fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' },
                  lineHeight: 1.05,
                  letterSpacing: '-0.5px',
                  mb: 2,
                }}
              >
                {marketDetails.name}
              </Typography>
            </Box>

            {/* Breadcrumbs */}
            <AppBreadcrumbs
              items={[
                { label: 'My Subscriptions', path: '/subscriptions' },
                { label: marketDetails.name, path: `/market/${marketId}` },
              ]}
              sx={{ mb: 4 }}
            />

            {/* Market Overview Section */}
            <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 2, md: 4 },
                }}
              >
                {/* Product Image Section */}
                <Box
                  sx={{
                    flexShrink: 0,
                    width: { xs: '100%', md: 'auto' },
                    maxWidth: '400px',
                    mx: { xs: 'auto', md: 0 },
                  }}
                >
                  {marketDetails.productImage ? (
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: '400px',
                        aspectRatio: '4/3',
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 2,
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      <img
                        src={marketDetails.productImage}
                        alt={marketDetails.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; font-size: 48px;">🏨</div>';
                          }
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: '400px',
                        aspectRatio: '4/3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      <HotelIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                    </Box>
                  )}
                </Box>

                {/* Market Information Section */}
                <Box sx={{ flex: 1 }}>
                  {/* Location Details */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Location Details
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Country:</strong> {marketDetails.country.name} ({marketDetails.country.code})
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Continent:</strong> {marketDetails.continent.name}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Market ID:</strong> {marketDetails.id}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* Subscription Information */}
                  {marketDetails.subscription && (
                    <>
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          Subscription Details
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>Product:</strong> {marketDetails.subscription.productName}
                        </Typography>
                        {marketDetails.subscription.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {marketDetails.subscription.description}
                          </Typography>
                        )}
                        {marketDetails.subscription.prices && marketDetails.subscription.prices.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {marketDetails.subscription.prices.map((price) => (
                              <Chip
                                key={price.priceId}
                                label={`${price.currency.toUpperCase()} ${price.amount / 100}/${price.interval}`}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                      <Divider sx={{ my: 3 }} />
                    </>
                  )}
                </Box>
              </Box>
            </Paper>

            {/* Market Content Tabs */}
            {marketIdNum && (
              <Box sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs 
                    value={tabValue} 
                    onChange={(event, newValue) => setTabValue(newValue)}
                    aria-label="market content tabs"
                  >
                    <Tab label="Available Reports" icon={<DescriptionIcon />} iconPosition="start" {...a11yProps(0)} />
                    <Tab label="Podcasts" icon={<HeadphonesIcon />} iconPosition="start" {...a11yProps(1)} />
                    <Tab label="Recent Key Findings" icon={<LightbulbIcon />} iconPosition="start" {...a11yProps(2)} />
                    <Tab label="Market Performance" icon={<TrendingUpIcon />} iconPosition="start" {...a11yProps(3)} />
                  </Tabs>
                </Box>

                {/* Available Reports Tab */}
                <TabPanel value={tabValue} index={0}>
                  <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
                    <LatestInsightsSection marketId={marketIdNum} />
                  </Paper>
                </TabPanel>

                {/* Podcasts Tab */}
                <TabPanel value={tabValue} index={1}>
                  <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
                    <PodcastsSection marketId={marketIdNum} />
                  </Paper>
                </TabPanel>

                {/* Recent Key Findings Tab */}
                <TabPanel value={tabValue} index={2}>
                  {marketKeyFindingsQuery.data?.findings && marketKeyFindingsQuery.data.findings.length > 0 ? (
                    <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <LightbulbIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h5" component="h2">
                          Recent Key Findings
                        </Typography>
                      </Box>
                      <KeyFindingsSection
                        reportId={0}
                        marketName={marketDetails.name}
                        findings={marketKeyFindingsQuery.data?.findings}
                        loading={marketKeyFindingsQuery.isLoading}
                        error={marketKeyFindingsQuery.error as Error | null}
                      />
                    </Paper>
                  ) : (
                    <Paper elevation={2} sx={{ p: 3, mt: 3, textAlign: 'center' }}>
                      <Typography variant="body1" color="text.secondary">
                        No key findings available for this market.
                      </Typography>
                    </Paper>
                  )}
                </TabPanel>

                {/* Market Performance Tab */}
                <TabPanel value={tabValue} index={3}>
                  <Box sx={{ mt: 3 }}>
                    <MarketPerformanceDashboard 
                      marketId={marketIdNum} 
                      marketName={marketDetails.name}
                    />
                  </Box>
                </TabPanel>
              </Box>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default MarketPage;